import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

/**
 * Toggles a service on and off hold. No confirmation is asked for, since holding a service changes none of its data.
 *
 * Input Props:
 * isOnHold (required): The current state of the service. Decides whether the button puts it on hold or takes it off.
 * onSetOnHold (required, function): Called to put the service on hold. May return a promise.
 * onTakeOffHold (required, function): Called to take the service off hold. May return a promise.
 * disabled: Disables the button.
 * sx: Styles for placing the button within its parent. Merged over the button's own styles.
 *
 * The button disables itself until the callback it ran has settled, so a second click can't send the opposite change
 * before the first one has landed.
 *
 */

const AuthServiceHoldToggleButton = props => {
  const { isOnHold, onSetOnHold, onTakeOffHold, disabled, sx } = props;

  const [isUpdating, setIsUpdating] = useState(false);
  // The callback may reload the page's data and unmount the button before it settles.
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const handleClick = async () => {
    setIsUpdating(true);
    try {
      await (isOnHold ? onTakeOffHold() : onSetOnHold());
    } finally {
      if (isMounted.current) setIsUpdating(false);
    }
  };

  return (
    <Button
      color="secondary"
      variant="contained"
      disabled={disabled || isUpdating}
      sx={{ backgroundColor: '#f0b11d', '&:hover': { backgroundColor: '#ba8a00' }, ...sx }}
      onClick={handleClick}
    >
      {isOnHold ? 'Take Off Hold' : 'Set On Hold'}
    </Button>
  );
};

AuthServiceHoldToggleButton.propTypes = {
  isOnHold: PropTypes.bool.isRequired,
  onSetOnHold: PropTypes.func.isRequired,
  onTakeOffHold: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default AuthServiceHoldToggleButton;
